import { useEffect, useRef, useState } from 'react';
import { Form, Input, Button, Alert } from 'antd';
import { MailOutlined } from '@ant-design/icons';

export interface PasswordResetFormProps {
  /**
   * Called when a valid email is submitted. Should resolve on success or reject on failure.
   */
  onSubmit: (email: string) => Promise<void>;
  /**
   * Optional default email value.
   */
  defaultEmail?: string;
}

const EMAIL_LABEL = 'メールアドレス';
const REQUIRED_MESSAGE = 'メールアドレスを入力してください。';
const INVALID_MESSAGE = '有効なメールアドレスを入力してください。';
const SUCCESS_MESSAGE =
  'パスワードリセットのリンクをメールで送信しました。メールをご確認ください。';
const FALLBACK_ERROR_MESSAGE = 'パスワードリセットに失敗しました。';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INLINE_ERROR_ID = 'password-reset-email-error';
const SUCCESS_REGION_ID = 'password-reset-success';

export default function PasswordResetForm({
  onSubmit,
  defaultEmail = '',
}: PasswordResetFormProps) {
  const [email, setEmail] = useState(defaultEmail);
  const [loading, setLoading] = useState(false);
  const [validationMode, setValidationMode] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const inlineErrorRef = useRef<HTMLDivElement | null>(null);
  const apiErrorRef = useRef<HTMLDivElement | null>(null);
  const successRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setEmail(defaultEmail);
    setValidationMode(false);
    setApiError(null);
    setSuccess(false);
  }, [defaultEmail]);

  const trimmedEmail = email.trim();
  const emailValidationMessage = trimmedEmail
    ? EMAIL_REGEX.test(trimmedEmail)
      ? null
      : INVALID_MESSAGE
    : REQUIRED_MESSAGE;
  const isEmailValid = !emailValidationMessage;
  const shouldShowInlineError = validationMode && !isEmailValid;
  const isSubmitDisabled = loading || !isEmailValid;

  useEffect(() => {
    if (shouldShowInlineError && inlineErrorRef.current) {
      inlineErrorRef.current.focus();
    }
  }, [shouldShowInlineError]);

  useEffect(() => {
    if (apiError && apiErrorRef.current) {
      apiErrorRef.current.focus();
    }
  }, [apiError]);

  useEffect(() => {
    if (success && successRef.current) {
      successRef.current.focus();
    }
  }, [success]);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    if (apiError) {
      setApiError(null);
    }
  };

  const handleSubmit = async () => {
    if (loading) {
      return;
    }

    setValidationMode(true);

    if (!isEmailValid) {
      return;
    }

    setLoading(true);
    setApiError(null);

    try {
      await onSubmit(trimmedEmail);
      setSuccess(true);
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : FALLBACK_ERROR_MESSAGE;
      setApiError(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        role="status"
        aria-live="polite"
        tabIndex={-1}
        id={SUCCESS_REGION_ID}
        ref={successRef}
        style={{ outline: 'none' }}
      >
        <Alert
          type="success"
          showIcon
          message="パスワードリセット"
          description={SUCCESS_MESSAGE}
        />
      </div>
    );
  }

  return (
    <Form layout="vertical" onFinish={handleSubmit} autoComplete="off">
      <Form.Item
        label={EMAIL_LABEL}
        required
        validateStatus={shouldShowInlineError ? 'error' : undefined}
        help={
          shouldShowInlineError ? (
            <div
              role="alert"
              aria-live="assertive"
              id={INLINE_ERROR_ID}
              tabIndex={-1}
              ref={inlineErrorRef}
              style={{ outline: 'none' }}
            >
              {emailValidationMessage}
            </div>
          ) : undefined
        }
      >
        <Input
          id="password-reset-email"
          type="email"
          value={email}
          onChange={handleEmailChange}
          onBlur={() => setValidationMode(true)}
          prefix={<MailOutlined />}
          placeholder="登録済みのメールアドレスを入力"
          size="large"
          disabled={loading}
          aria-invalid={shouldShowInlineError ? 'true' : 'false'}
          aria-describedby={shouldShowInlineError ? INLINE_ERROR_ID : undefined}
          required
        />
      </Form.Item>

      {apiError && (
        <div
          role="alert"
          aria-live="assertive"
          tabIndex={-1}
          ref={apiErrorRef}
          style={{ marginBottom: 16, outline: 'none' }}
        >
          <Alert message="エラー" description={apiError} type="error" showIcon />
        </div>
      )}

      <Form.Item style={{ marginBottom: 0 }}>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          disabled={isSubmitDisabled}
          aria-busy={loading ? 'true' : 'false'}
          size="large"
          block
        >
          {loading ? '送信中...' : 'リセットリンクを送信'}
        </Button>
      </Form.Item>
    </Form>
  );
}
